import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import {
  auChipSx,
  AU_BACKDROP_SX,
  AU_DIALOG_PAPER_SX,
  AU_PAGINATION_SX,
  AU_RADIUS_CONTROL,
  AU_SCROLLBAR_SX,
} from '@/theme/au-compat';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import DeviceNameSpan from '@components/deviceNameSpan';
import GlobalModal from '@components/GlobalModal';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import TimeElapsed from '@components/TimeElapsed';
import TruncatedTypography from '@components/TruncatedTypography';
import { Access } from '@features/authorization';
import { useGateMovementsHistory } from '@features/gateCommon/hooks/useGateMovementsHistory';
import { describeMovementStatus } from '@features/gateCommon/utils/gateMovementState';
import StandardListPagination from '@features/standardDesign/components/listView/StandardListPagination';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Skeleton, Typography } from '@mui/material';
import { type CSSProperties, type FC, type ReactNode, Suspense, useState } from 'react';
import { MovementPosition } from '../movementsTable/MovementPosition';
import { Table } from '../movementsTable/Table';

const DEFAULT_ROWS_PER_PAGE = 20;

/**
 * El chip de estado de una fila: rótulo y tono salen de `describeMovementStatus` (AMARU-897/US-006).
 * Los rótulos del código de cierre son más largos que los del estado: el chip no pasa del ancho de
 * su celda y el texto completo queda en el `title`.
 */
const RowStateChip: FC<{
  movement: Parameters<typeof describeMovementStatus>[0];
  now: number;
}> = ({ movement, now }) => {
  const { i18n } = useLingui();
  const { label, tone } = describeMovementStatus(movement, i18n, now);
  return (
    <Box sx={{ ...auChipSx(tone), maxWidth: '100%' }} title={label}>
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Box>
    </Box>
  );
};

/**
 * El reparto de las siete columnas, portado de `.oc-movements-table[data-detailed]
 * .oc-movements-row` (`operar-compuerta.css:594-599`).
 *
 * **Todas en `fr` con su mínimo, ninguna en `auto`.** Con `auto` cada columna mide su propio
 * contenido, así que el ancho de «Solicitada» dependería de si algún número tiene tres cifras y la
 * fila entera se re-repartiría al cambiar un dato. El ID lleva el mínimo más chico —son dígitos de
 * ancho fijo y no tiene nada que elidir—, y lo que le sobra se lo queda la columna de usuario, que
 * sí elide.
 */
const GRID_TEMPLATE_COLUMNS = [
  'minmax(64px, 0.5fr)',
  'minmax(140px, 1.4fr)',
  'minmax(110px, 1fr)',
  'minmax(80px, 0.7fr)',
  'minmax(130px, 1.1fr)',
  'minmax(130px, 1.1fr)',
  'minmax(120px, 1fr)',
].join(' ');

/** El aire entre las siete columnas, de la misma regla del espécimen que el reparto de arriba. */
const GRID_GAP = '14px';

/** El alto de una fila y el del encabezado, medidos en el espécimen (`operar-compuerta.css:666`). */
const ROW_HEIGHT_PX = 38;
const HEAD_HEIGHT_PX = 30;

/** El hueco de lo que **todavía no pasó** — distinto de `??`, que es lo que nunca se reportó. */
const PendingCell: FC = () => (
  <Typography
    sx={{
      fontSize: 11.5,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      color: 'text.secondary',
      fontStyle: 'italic',
    }}
  >
    <Trans>Pendiente</Trans>
  </Typography>
);

const HeaderCell: FC<{ children: ReactNode; align?: 'left' | 'center' }> = ({
  children,
  align = 'center',
}) => (
  /* Un `Box` y no un `span`: `text-align` sobre una caja inline alinea el contenido *dentro* de
     ella, y la caja de un `span` mide lo que mide su texto — no se centra en la celda. Es el modo
     de fallar que este reparto tiene: la alineación se declara y no pasa nada, sin ruido. */
  <Box sx={{ textAlign: align }}>{children}</Box>
);

const ValueCell: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    sx={{ fontSize: 11.5, textAlign: 'center', whiteSpace: 'nowrap', color: 'text.primary' }}
  >
    {children}
  </Typography>
);

interface Props {
  gateId: string;
}

/**
 * El listado paginado, sin la cáscara del diálogo.
 *
 * Exportado para el test: la cáscara arrastra el estado global de modales y el gate de
 * autorización, que no son lo que esta tabla promete —siete columnas, los dos huecos distintos y
 * un alto que no salta—, y montarla para verificar eso pedía media app en pie.
 */
export const PaginatedHistoryContent: FC<Props> = ({ gateId }) => {
  const { formatMetricValue, formatDateTime } = useFormatters();
  const { i18n } = useLingui();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { items, pageInfo, unit, precision } = useGateMovementsHistory(gateId, page, rowsPerPage);

  const formatPosition = (position: number) => formatMetricValue(position, { unit, precision });

  /**
   * El alto lo reserva **la página más llena que puede haber**, no las filas que llegaron: sin
   * esto la última página —tres filas de veinte— encoge el diálogo y el paginador salta a mitad de
   * pantalla justo cuando hay que volver a tocarlo.
   *
   * Y es el mínimo contra el total, no el tamaño de página a secas: con veinte por página sobre
   * seis movimientos, reservar por el tamaño abre catorce filas de aire.
   */
  const reservedRows = Math.min(rowsPerPage, pageInfo.totalCount);

  const headers = [
    <HeaderCell key="id">
      <Trans>ID</Trans>
    </HeaderCell>,
    <HeaderCell key="user" align="left">
      <Trans>Solicitado por</Trans>
    </HeaderCell>,
    /* La columna lleva dos números, así que el encabezado dice cuál es cuál. Es una segunda línea
       atenuada y no una fila de sub-encabezados: «Inicial → Final» se lee de corrido. */
    <Box
      key="position"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}
    >
      <Box component="span">
        <Trans>Posición</Trans>
      </Box>
      <Box component="span" sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>
        <Trans>Inicial → Final</Trans>
      </Box>
    </Box>,
    <HeaderCell key="requested">
      <Trans>Solicitada</Trans>
    </HeaderCell>,
    <HeaderCell key="requestedAt">
      <Trans>Fecha solicitud</Trans>
    </HeaderCell>,
    <HeaderCell key="finishedAt">
      <Trans>Fecha finalización</Trans>
    </HeaderCell>,
    /* «Estado operación» y no «Estado solicitud»: es el rótulo exacto del widget de operación
       vecino, y la columna muestra su mismo `MovementState`. */
    <HeaderCell key="state">
      <Trans>Estado operación</Trans>
    </HeaderCell>,
  ];

  // El historial no lleva reloj: el vencimiento se evalúa al pintar la página.
  const now = Date.now();
  const rows = items.map((mov) => ({
    columns: [
      /* `tabular-nums` porque es una columna de números de igual largo leídos en vertical: sin
         ancho fijo de dígito los de una fila no caen bajo los de la siguiente. Atenuado porque
         identifica la fila, no la califica — el dato que se lee es el resto. */
      <Typography
        key="id"
        sx={{
          fontSize: 11.5,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {mov.id}
      </Typography>,
      <Box key="user" sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
        {mov.requestedBy.name ? (
          <TruncatedTypography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
            {mov.requestedBy.name}
          </TruncatedTypography>
        ) : (
          <TruncatedTypography
            sx={{ fontSize: 12, fontWeight: 700, fontStyle: 'italic', lineHeight: 1.2 }}
          >
            {/* Sin `id` no hubo usuario de Amaru detrás: es una operación manual —la que abre el
                operario en terreno—, no un usuario que no se pudo resolver (AMARU-862). */}
            {mov.requestedBy.id == null ? (
              <Trans>Operación manual</Trans>
            ) : (
              <Trans>Usuario desconocido</Trans>
            )}
          </TruncatedTypography>
        )}
        <TruncatedTypography sx={{ fontSize: 10.5, lineHeight: 1.2, color: 'text.secondary' }}>
          <TimeElapsed timestamp={mov.requestedAt} />
        </TruncatedTypography>
      </Box>,
      /* `!= null` sobre la **tupla** y no sobre el valor: una posición de 0 cm —compuerta
         cerrada— es un dato válido y no un hueco. Ver `MovementPosition`. */
      <MovementPosition
        key="position"
        detailed
        from={mov.startPosition != null ? mov.startPosition[1] : null}
        to={mov.finishPosition != null ? mov.finishPosition[1] : null}
        requested={mov.requestedPosition != null ? mov.requestedPosition[1] : null}
        format={formatPosition}
        closed={mov.finishedAt != null}
      />,
      /* Una operación manual no tiene destino solicitado; la fecha de solicitud sí existe siempre y
         viene por `requestedAt`, no por el punto solicitado (AMARU-862). */
      mov.requestedPosition != null ? (
        <ValueCell key="requested">{formatPosition(mov.requestedPosition[1])}</ValueCell>
      ) : (
        <ValueCell key="requested">
          <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            <Trans>Sin destino</Trans>
          </Box>
        </ValueCell>
      ),
      <ValueCell key="requestedAt">{formatDateTime(mov.requestedAt)}</ValueCell>,
      // La fecha sale de `finishedAt` y no de la tupla de posición final: una solicitud cancelada o
      // vencida cierra sin posición, y leerla de la tupla la dejaba «Pendiente» ya cerrada.
      mov.finishedAt != null ? (
        <ValueCell key="finishedAt">{formatDateTime(mov.finishedAt)}</ValueCell>
      ) : (
        <PendingCell key="finishedAt" />
      ),
      <Box key="state" sx={{ display: 'grid', justifyItems: 'center' }}>
        {/* El chip de la capa en su `subtle`: la tabla ya tiene su propia línea entre filas, y un
            borde acá sumaba una segunda separación sobre la misma junta. El tono sale de
            `stateTone` y no de `getStateColor`, que devuelve rellenos sólidos para marcas
            gráficas (D-003 del PR 1). */}
        <RowStateChip movement={mov} now={now} />
      </Box>,
    ],
  }));

  return (
    /* Las tres zonas: el encabezado se pega arriba, el área de scroll sostiene **solo las filas**,
       y el paginador queda afuera. Sin esa separación una página de veinte filas manda el diálogo
       entero a scroll y el paginador —que es lo que hay que volver a tocar— se va con él. */
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minHeight: 0 }}>
      <Box
        // La custom property viaja desde acá para que la aritmética viva junto al alto de fila del
        // que depende, igual que en la hoja del espécimen.
        style={{ '--oc-movements-rows': reservedRows } as CSSProperties}
        sx={{
          overflow: 'auto',
          maxHeight: '52vh',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: AU_RADIUS_CONTROL,
          ...AU_SCROLLBAR_SX,
        }}
      >
        <Table
          label={i18n._(msg`Movimientos de la compuerta`)}
          headers={headers}
          rows={rows}
          gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
          gap={GRID_GAP}
          stickyHeader
          sx={{
            minHeight: `calc(var(--oc-movements-rows) * ${ROW_HEIGHT_PX}px + ${HEAD_HEIGHT_PX}px)`,
          }}
        />
      </Box>
      <StandardListPagination
        totalItems={pageInfo.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        // Sin `setPage(1)` a mano: `StandardListPagination` ya vuelve a la primera al cambiar el
        // tamaño, y duplicarlo acá era dos fuentes para el mismo reset.
        onRowsPerPageChange={setRowsPerPage}
        slotProps={{
          /* Pisan los defaults responsive del componente, que en ≥md pone `large` y enciende los
             saltos a primera y última. Con el rango completo a la vista los extremos ya están
             dibujados como números, así que ese par de flechas dobles repite dos destinos que ya
             se pueden tocar. */
          pagination: {
            size: 'small',
            showFirstButton: false,
            showLastButton: false,
            siblingCount: 1,
            boundaryCount: 1,
            sx: AU_PAGINATION_SX,
          },
        }}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
};

/**
 * El chrome del diálogo entra por `dialogProps`, siguiendo `MoveGateModal.auDialogProps`.
 *
 * `maxWidth: false` y un ancho en píxeles: los siete mínimos de la grilla suman ~774px más los
 * seis huecos de 14 y el padding lateral, así que el `xl` del breakpoint de MUI dejaba la tabla
 * flotando en un diálogo el doble de ancho que su contenido.
 *
 * El número es el `lg` de MUI, un escalón por encima del ~`md` que tenía: con siete columnas y dos
 * de ellas fechas completas, el mínimo de la grilla deja poco margen antes de que las columnas
 * elidan, y el escalón se lo devuelve a las que estiran —«Solicitado por» primero, que es la única
 * de largo variable—.
 *
 * **Sin barra de acciones.** El diálogo es de lectura: no confirma ni descarta nada, así que el
 * único «Cerrar» que necesita es la cruz que `ModalTitle` ya pone en la cabecera. Un pie con un
 * botón que repite esa cruz gasta una franja entera del alto —el que la tabla venía de pelear en
 * US-003— para no ofrecer una salida nueva.
 */
const auDialogProps = {
  maxWidth: false as const,
  PaperProps: { sx: { ...AU_DIALOG_PAPER_SX, width: '100%', maxWidth: 1200 } },
  slotProps: { backdrop: { sx: AU_BACKDROP_SX } },
};

export const GateMovementsHistoryModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('gateMovementsHistoryModal');

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={auDialogProps}
      authorization={
        state.open
          ? {
              access: Access.ViewGateMovementsHistory,
              deviceId: state.gateId,
            }
          : undefined
      }
    >
      <ModalTitle
        onClose={onClose}
        title={<Trans>Ver historial de movimientos</Trans>}
        subtitle={
          state.open ? (
            <DeviceNameSpan
              deviceId={state.gateId}
              variant="subtitle1"
              color="text.secondary"
              lineHeight={1}
            />
          ) : null
        }
      />
      {/* Sin alto fijo: el alto lo reserva la tabla por su tamaño de página y el `maxHeight` del
          área de scroll lo acota contra el viewport. Los 600px de antes abrían aire con pocos
          movimientos y no evitaban nada con muchos.

          `bottomDivider={false}` porque los dos filos internos del diálogo son los `dividers` de
          este `DialogContent`, y el de abajo es el que separa del pie. Retirado el pie, ese filo
          queda como una línea suelta apoyada contra la esquina redondeada del Paper. */}
      <ModalContent bottomDivider={false} sx={{ display: 'flex', flexDirection: 'column' }}>
        {state.open && state.gateId && (
          <ApolloErrorBoundary variant="section-alert">
            <Suspense
              fallback={
                <Skeleton
                  variant="rounded"
                  sx={{
                    height: DEFAULT_ROWS_PER_PAGE * ROW_HEIGHT_PX + HEAD_HEIGHT_PX,
                    // El mismo tope que el área de scroll, para que el diálogo no cambie de alto
                    // entre el esqueleto y la tabla.
                    maxHeight: '52vh',
                  }}
                />
              }
            >
              <PaginatedHistoryContent gateId={state.gateId} />
            </Suspense>
          </ApolloErrorBoundary>
        )}
      </ModalContent>
    </GlobalModal>
  );
};
